import { useState } from "react";
import { Leaf, Sparkles, Palette } from "lucide-react";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { Container, PageHeader, Cta } from "../components/ui-kit";
import { COLOR_CHARTS } from "../mock";

const chartIcons = { standart: Palette, ozel: Sparkles, ekolojik: Leaf };
const T = "/images/thumbs";

const Colors = () => {
  const [tab, setTab] = useState("standart");
  const chart = COLOR_CHARTS.find((c) => c.id === tab);

  return (
    <main className="bg-canvas min-h-screen" data-testid="colors-page">
      <Seo
        title="Renk Kartelası | 48 Galvanik Kaplama Rengi — Koşan Düğme"
        description="Standart, special ve ekolojik gruplarda toplam 48 galvanik kaplama rengi. Tüm ürün gruplarımız renk kartelamızdaki her renkte üretilebilir."
        path="/renkler"
      />
      <Breadcrumbs items={[{ label: "Renk Kartelası", to: "/renkler" }]} />
      <PageHeader
        eyebrow="Galvanik Kaplama"
        title="Renk kartelası"
        text="Standart, special ve ekolojik gruplarda toplam 48 kaplama rengi. Tüm ürün gruplarımız kartelamızdaki her renkte üretilebilir."
      >
        <div className="hidden lg:flex justify-end gap-3" data-testid="colors-header-dots">
          {["101", "104", "113", "124", "155", "166"].map((c) => (
            <div key={c} className="w-14 h-14 rounded-full overflow-hidden bg-plate ring-1 ring-line">
              <img src={`${T}/ksn_${c}.jpg`} alt={`KSN ${c}`} className="w-full h-full object-cover scale-[1.15]" />
            </div>
          ))}
        </div>
      </PageHeader>

      {/* Sekmeler */}
      <div className="sticky top-[78px] z-30 bg-canvas/90 backdrop-blur-xl border-b border-line" data-testid="colors-tabs">
        <Container className="flex gap-8 overflow-x-auto no-scrollbar">
          {COLOR_CHARTS.map((c) => {
            const Icon = chartIcons[c.id];
            const on = tab === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setTab(c.id)}
                data-testid={`color-tab-${c.id}`}
                className={`relative shrink-0 inline-flex items-center gap-2.5 py-5 text-[12px] font-semibold tracking-[0.14em] uppercase transition-colors ${
                  on ? "text-ink" : "text-mute hover:text-ink"
                }`}
              >
                <Icon size={15} strokeWidth={1.6} className={on ? "text-brand" : ""} />
                {c.name}
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${on ? "bg-ink text-white" : "bg-sand text-mute"}`}>{c.colors.length}</span>
                <span className={`absolute left-0 right-0 bottom-0 h-[2px] bg-brand transition-transform duration-300 origin-left ${on ? "scale-x-100" : "scale-x-0"}`} />
              </button>
            );
          })}
        </Container>
      </div>

      <Container className="py-14 lg:py-20">
        <Reveal key={tab}>
          <div className="grid lg:grid-cols-12 gap-6 items-end mb-12">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3">
                <span className="w-6 h-px bg-brand" />
                <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-brand">{chart.colors.length} renk</span>
              </div>
              <h2 className="font-display font-light text-3xl sm:text-4xl text-ink mt-3" data-testid="colors-chart-title">
                {chart.title}
              </h2>
            </div>
            <p className="lg:col-span-4 text-ink2 text-[14px] leading-relaxed">{chart.desc}</p>
          </div>
        </Reveal>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-10" key={`grid-${tab}`} data-testid="colors-grid">
          {chart.colors.map((c, i) => (
            <Reveal key={c.code} delay={(i % 6) * 45}>
              <div className="group text-center" data-testid={`color-swatch-${c.code.replace(" ", "-")}`}>
                <div className="aspect-square rounded-full overflow-hidden bg-plate ring-1 ring-line group-hover:ring-ink/40 transition-all duration-300 shadow-[0_0_0_0_rgba(0,0,0,0)] group-hover:shadow-[0_18px_40px_rgba(26,26,26,0.10)]">
                  <img src={c.image} alt={c.code} loading="lazy" className="w-full h-full object-cover scale-[1.15] group-hover:scale-125 transition-transform duration-700" />
                </div>
                <p className="text-[11px] font-semibold tracking-[0.18em] text-ink mt-4 group-hover:text-brand transition-colors">{c.code}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-20 bg-paper border border-line p-8 sm:p-12 grid lg:grid-cols-12 gap-8 items-center" data-testid="colors-cta">
            <div className="lg:col-span-8">
              <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-brand">Numune</p>
              <h3 className="font-display font-light text-2xl sm:text-3xl text-ink mt-3">Renk eşleştirmesi veya numune mi gerekiyor?</h3>
              <p className="text-ink2 text-[14px] sm:text-[15px] mt-3 max-w-xl leading-relaxed">
                Kumaşınıza ve koleksiyonunuza özel renk çalışması yapıyoruz. Numune talebinizi iletin, size özel hazırlayalım.
              </p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <Cta to="/iletisim" data-testid="colors-sample-cta">Numune talep et</Cta>
            </div>
          </div>
        </Reveal>
      </Container>
    </main>
  );
};

export default Colors;
